import React, { useEffect, useRef, useState } from "react";
import { initHighchartsModules } from "../../../../../utils/initHighchartsModules";

import AccordionComponent from "../../../../../common/Accordion";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartExportButton from "../../../../../common/HighchartExportButton";

import TopLimitDropdown from "../../../../../common/TopLimitDropdown";
import MonthIntervalDropdown from "../../../../../common/MonthIntervalDropdown";
import { getHistoryReviewed } from "../../../../../services/HistorianDashboardAPI";

interface HistoryReviewdProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
  userRole?: any;
}

const HistoryReviewdSummary: React.FC<HistoryReviewdProps> = ({
  openDropdown,
  setOpenDropdown,
  userRole,
}) => {
  const [topCount, setTopCount] = useState<number>(5);
  const [monthInterval, setMonthInterval] = useState<string>("1");
  const [topLabel, setTopLabel] = useState<string>("Top 5");
  const [intervalLabel, setIntervalLabel] = useState<string>("This Month");
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

  useEffect(() => {
    initHighchartsModules();
  }, []);

  const fetchHistoryReviewData = async () => {
    try {
      const result = await getHistoryReviewed(topCount, monthInterval);
      setSummaryData(result);
    } catch (error) {
      console.error("Failed to load case summary data", error);
    }
  };

  useEffect(() => {
    fetchHistoryReviewData();
  }, [topCount, monthInterval]);

  const buildPagesChartOptions = (data: any[]): Highcharts.Options => {
    return {
      chart: {
        type: "bar",
        height: 300, // dynamic height based on doctors
      },

      title: {
        text: undefined,
      },

      xAxis: {
        categories: data.map((item) => item.qmeDoctorName),
        title: {
          text: "Doctors",
        },
      },

      yAxis: {
        min: 0,
        title: {
          text: "Pages",
        },
        gridLineWidth: 1,
      },

      tooltip: {
        pointFormat: "<b>{point.y} pages</b>",
      },

      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            formatter: function (this: Highcharts.Point) {
              return this.y;
            },
            style: {
              fontWeight: "bold",
            },
          },
        },
      },

      legend: {
        enabled: false,
      },

      series: [
        {
          type: "bar",
          name: "Pages Reviewed",
          data: data.map((item) => item.totalHistoryCompleted),
          color: "#22c55e", // green like image
        },
      ],

      credits: { enabled: false },
    };
  };

  const handleTopChange = (value: number, label: string) => {
    setTopCount(value);
    setTopLabel(label);
  };

  const handleIntervalChange = (value: string | number, label: string) => {
    setMonthInterval(String(value));
    setIntervalLabel(label);
  };

  const [caseSummaryKey, setCaseSummaryKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (caseSummaryKey === key) {
      return; // do nothing
    }
    setCaseSummaryKey(key || "0");
  };

  const summaryAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={caseSummaryKey}
      onSelect={handleBodyAccordionSelect}
      isDashboard={true}
    />
  );

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    const closeAll = () => setOpenDropdown(null);
    window.addEventListener("closeAllDropdowns", closeAll);
    return () => window.removeEventListener("closeAllDropdowns", closeAll);
  }, []);

  const hasNoData =
    summaryData.length === 0 ||
    summaryData.every(
      (item) =>
        item.responseCode === 0 || Number(item.totalHistoryCompleted) === 0,
    );

  return (
    <div className="bg-white rounded-lg shadow-md w-100 h-100">
      {summaryAccordion([
        {
          title: (
            <div
              className="d-flex  align-items-center w-100 h-100 "
              onClick={(e) => e.stopPropagation()}
            >
              <span className="me-auto">Histories Prepared For Doctor</span>

              <div className="d-flex gap-2">
                <>
                  <TopLimitDropdown
                    title={topLabel}
                    onSelect={handleTopChange}
                    show={openDropdown === "rorPagesLimit"}
                    onToggle={(isOpen) =>
                      setOpenDropdown(isOpen ? "rorPagesLimit" : null)
                    }
                  />
                </>

                <MonthIntervalDropdown
                  title={intervalLabel}
                  onSelect={handleIntervalChange}
                  show={openDropdown === "rorPagesInterval"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "rorPagesInterval" : null)
                  }
                />
              </div>
              <HighchartExportButton chartRef={chartComponentRef} />
            </div>
          ),
          content: (
            <>
              <>
                {hasNoData ? (
                  <div className="text-center">
                    {/* <img src="/noChart.png" alt="noChart" className="nodata" /> */}
                    <div className="nodata_wrapper">
                      <span className="material-symbols-outlined">
                        bar_chart_off
                      </span>{" "}
                      No Data Available
                    </div>
                  </div>
                ) : (
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={buildPagesChartOptions(summaryData)}
                    ref={chartComponentRef}
                  />
                )}
              </>
            </>
          ),
        },
      ])}
    </div>
  );
};

export default HistoryReviewdSummary;
